// attendanceSlice.js - UPDATED for simplified backend structure
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchLiveAttendance,
  fetchTodaySummary,
  fetchMyAttendance,
  fetchEmployeeSummary,
  updateAttendanceStatus,
  createAttendance,
  deleteAttendance,
  bulkUpdateAttendance,
} from './attendanceThunks';

const initialState = {
  // Today's Summary (Dashboard)
  todaySummary: {
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    onLeave: 0,
    medicalLeave: 0,
    workFromHome: 0,
    totalWorkHours: 0,
    avgWorkHours: 0,
    totalEmployees: 0,
    employeesWithRecords: 0,
    employeesWithoutRecords: 0,
    date: null,
    records: [],
  },
  todayLoading: false,
  todayError: null,

  // My Attendance (Current User)
  myAttendance: {
    employeeId: null,
    employeeName: '',
    employeeCode: '',
    department: '',
    role: '',
    records: [],
    summary: {
      present: 0,
      absent: 0,
      late: 0,
      halfDay: 0,
      onLeave: 0,
      medicalLeave: 0,
      totalRecords: 0,
      totalWorkHours: 0,
      avgWorkHours: 0,
    },
    count: 0,
  },
  myAttendanceLoading: false,
  myAttendanceError: null,

  // Employee Summary (Manager/HR View)
  employeeSummary: {
    employeeId: null,
    employeeName: '',
    employeeCode: '',
    department: '',
    role: '',
    records: [],
    summary: {
      present: 0,
      absent: 0,
      late: 0,
      halfDay: 0,
      onLeave: 0,
      medicalLeave: 0,
      totalRecords: 0,
      totalWorkHours: 0,
      avgWorkHours: 0,
    },
    count: 0,
    totalEmployees: 0,
  },
  employeeSummaryLoading: false,
  employeeSummaryError: null,

  // Live Attendance
  liveAttendance: [],
  liveLoading: false,
  liveError: null,

  // Filters for My Attendance
  myFilters: {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: '',
  },

  // Filters for Employee Summary
  summaryFilters: {
    employeeId: null,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: '',
  },
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    // Set filters for my attendance
    setMyFilters: (state, action) => {
      state.myFilters = { ...state.myFilters, ...action.payload };
    },

    // Clear my attendance filters
    clearMyFilters: (state) => {
      state.myFilters = {
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        status: '',
      };
    },

    // Set filters for employee summary
    setSummaryFilters: (state, action) => {
      state.summaryFilters = { ...state.summaryFilters, ...action.payload };
    },

    // Clear employee summary filters
    clearSummaryFilters: (state) => {
      state.summaryFilters = {
        employeeId: null,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        status: '',
      };
    },

    // Clear employee summary data
    clearEmployeeSummary: (state) => {
      state.employeeSummary = {
        employeeId: null,
        employeeName: '',
        employeeCode: '',
        department: '',
        role: '',
        records: [],
        summary: {
          present: 0,
          absent: 0,
          late: 0,
          halfDay: 0,
          onLeave: 0,
          medicalLeave: 0,
          totalRecords: 0,
          totalWorkHours: 0,
          avgWorkHours: 0,
        },
        count: 0,
        totalEmployees: 0,
      };
      state.employeeSummaryError = null;
    },

    // Update local attendance status (optimistic update)
    updateLocalStatus: (state, action) => {
      const { recordId, newStatus } = action.payload;
      
      // Update in today's records
      const todayRecord = state.todaySummary.records.find(r => r.id === recordId);
      if (todayRecord) {
        todayRecord.status = newStatus;
      }
      
      // Update in my attendance records
      const myRecord = state.myAttendance.records.find(r => r.id === recordId);
      if (myRecord) {
        myRecord.status = newStatus;
      }
      
      // Update in employee summary records
      const empRecord = state.employeeSummary.records.find(r => r.id === recordId);
      if (empRecord) {
        empRecord.status = newStatus;
      }
    },
  },
  extraReducers: (builder) => {
    // ==================== TODAY'S SUMMARY ====================
    builder
      .addCase(fetchTodaySummary.pending, (state) => {
        state.todayLoading = true;
        state.todayError = null;
      })
      .addCase(fetchTodaySummary.fulfilled, (state, action) => {
        state.todayLoading = false;
        state.todaySummary = action.payload;
      })
      .addCase(fetchTodaySummary.rejected, (state, action) => {
        state.todayLoading = false;
        state.todayError = action.payload || 'Failed to fetch today\'s summary';
      });

    // ==================== MY ATTENDANCE ====================
    builder
      .addCase(fetchMyAttendance.pending, (state) => {
        state.myAttendanceLoading = true;
        state.myAttendanceError = null;
      })
      .addCase(fetchMyAttendance.fulfilled, (state, action) => {
        state.myAttendanceLoading = false;
        state.myAttendance = action.payload;
      })
      .addCase(fetchMyAttendance.rejected, (state, action) => {
        state.myAttendanceLoading = false;
        state.myAttendanceError = action.payload || 'Failed to fetch your attendance';
      });

    // ==================== EMPLOYEE SUMMARY ====================
    builder
      .addCase(fetchEmployeeSummary.pending, (state) => {
        state.employeeSummaryLoading = true;
        state.employeeSummaryError = null;
      })
      .addCase(fetchEmployeeSummary.fulfilled, (state, action) => {
        state.employeeSummaryLoading = false;
        state.employeeSummary = action.payload;
      })
      .addCase(fetchEmployeeSummary.rejected, (state, action) => {
        state.employeeSummaryLoading = false;
        state.employeeSummaryError = action.payload || 'Failed to fetch employee summary';
      });

    // ==================== LIVE ATTENDANCE ====================
    builder
      .addCase(fetchLiveAttendance.pending, (state) => {
        state.liveLoading = true;
        state.liveError = null;
      })
      .addCase(fetchLiveAttendance.fulfilled, (state, action) => {
        state.liveLoading = false;
        state.liveAttendance = action.payload;
      })
      .addCase(fetchLiveAttendance.rejected, (state, action) => {
        state.liveLoading = false;
        state.liveError = action.payload || 'Failed to fetch live attendance';
      });

    // ==================== UPDATE ATTENDANCE STATUS ====================
    builder
      .addCase(updateAttendanceStatus.pending, (state) => {
        // Status already updated optimistically
      })
      .addCase(updateAttendanceStatus.fulfilled, (state, action) => {
        // Update confirmed from server
        const updatedRecord = action.payload;
        
        // Update in today's records
        const todayIndex = state.todaySummary.records.findIndex(r => r.id === updatedRecord.id);
        if (todayIndex !== -1) {
          state.todaySummary.records[todayIndex] = updatedRecord;
        }
        
        // Update in my attendance
        const myIndex = state.myAttendance.records.findIndex(r => r.id === updatedRecord.id);
        if (myIndex !== -1) {
          state.myAttendance.records[myIndex] = updatedRecord;
        }
        
        // Update in employee summary
        const empIndex = state.employeeSummary.records.findIndex(r => r.id === updatedRecord.id);
        if (empIndex !== -1) {
          state.employeeSummary.records[empIndex] = updatedRecord;
        }
      })
      .addCase(updateAttendanceStatus.rejected, (state, action) => {
        // Revert optimistic update on failure
        if (action.meta?.arg) {
          const { recordId, oldStatus } = action.meta.arg;
          
          const todayRecord = state.todaySummary.records.find(r => r.id === recordId);
          if (todayRecord && oldStatus) {
            todayRecord.status = oldStatus;
          }
          
          const myRecord = state.myAttendance.records.find(r => r.id === recordId);
          if (myRecord && oldStatus) {
            myRecord.status = oldStatus;
          }
          
          const empRecord = state.employeeSummary.records.find(r => r.id === recordId);
          if (empRecord && oldStatus) {
            empRecord.status = oldStatus;
          }
        }
      });

    // ==================== CREATE ATTENDANCE ====================
    builder
      .addCase(createAttendance.fulfilled, (state, action) => {
        // Add new record to today's list if it's today
        const newRecord = action.payload;
        const today = new Date().toISOString().split('T')[0];
        if (newRecord.date === today) {
          state.todaySummary.records.push(newRecord);
        }
      });

    // ==================== DELETE ATTENDANCE ====================
    builder
      .addCase(deleteAttendance.fulfilled, (state, action) => {
        const deletedId = action.payload;
        
        // Remove from today's records
        state.todaySummary.records = state.todaySummary.records.filter(r => r.id !== deletedId);
        
        // Remove from my attendance
        state.myAttendance.records = state.myAttendance.records.filter(r => r.id !== deletedId);
        
        // Remove from employee summary
        state.employeeSummary.records = state.employeeSummary.records.filter(r => r.id !== deletedId);
      });
  },
});

export const {
  setMyFilters,
  clearMyFilters,
  setSummaryFilters,
  clearSummaryFilters,
  clearEmployeeSummary,
  updateLocalStatus,
} = attendanceSlice.actions;

export default attendanceSlice.reducer;

// Selectors
export const selectTodaySummary = (state) => state.attendance.todaySummary;
export const selectTodayLoading = (state) => state.attendance.todayLoading;
export const selectTodayError = (state) => state.attendance.todayError;

export const selectMyAttendance = (state) => state.attendance.myAttendance;
export const selectMyAttendanceLoading = (state) => state.attendance.myAttendanceLoading;
export const selectMyAttendanceError = (state) => state.attendance.myAttendanceError;

export const selectEmployeeSummary = (state) => state.attendance.employeeSummary;
export const selectEmployeeSummaryLoading = (state) => state.attendance.employeeSummaryLoading;
export const selectEmployeeSummaryError = (state) => state.attendance.employeeSummaryError;

export const selectLiveAttendance = (state) => state.attendance.liveAttendance;
export const selectLiveLoading = (state) => state.attendance.liveLoading;